import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../../../api/client';
import { usePagination } from '../../../hooks/usePagination';
import { Pagination } from '../../../components/shared/Pagination';

interface AuditEntry {
  module: string;
  action: string;
  details: string;
  created_at: string;
}

export function AuditTab({ t }: { t: (k: string) => string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const pag = usePagination('settings');

  const loadAudit = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(pag.page), per_page: String(pag.perPage) });
    if (moduleFilter) params.set('module', moduleFilter);
    if (actionFilter) params.set('action', actionFilter);
    const res = await api.get<any>(`/audit?${params}`);
    if (res.success) {
      setEntries(res.data || []);
      pag.setTotal((res as any).meta?.total || 0);
    }
    setLoading(false);
  }, [pag.page, pag.perPage, moduleFilter, actionFilter]);

  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { pag.resetPage(); }, [moduleFilter, actionFilter]);

  const MODULE_COLORS: Record<string, string> = {
    inventory: 'text-cyan-500', purchases: 'text-purple-500', sales: 'text-orange-500',
    warehouse: 'text-green-500', catalog: 'text-blue-500', auth: 'text-red-500',
    config: 'text-yellow-500', finance: 'text-emerald-500',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.audit')}</h2>
        <button onClick={loadAudit} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          value={moduleFilter}
          onChange={e => setModuleFilter(e.target.value)}
          placeholder="Filtrar modulo..."
          className="px-3 py-1.5 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] w-40"
        />
        <input
          type="text"
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          placeholder="Filtrar accion..."
          className="px-3 py-1.5 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] w-40"
        />
        {pag.total > 0 && (
          <span className="text-xs text-[var(--color-text-muted)] self-center">{pag.total} registros</span>
        )}
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)] py-8 text-center">Sin registros de auditoria</p>
      ) : (
        <>
          <div className="divide-y divide-[var(--color-border)] border border-[var(--color-border)] rounded-lg overflow-hidden">
            {entries.map((entry, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-[var(--color-surface-hover)]">
                <span className={`text-xs font-mono w-24 shrink-0 ${MODULE_COLORS[entry.module] || 'text-[var(--color-primary)]'}`}>
                  {entry.module}
                </span>
                <span className="text-xs font-medium text-[var(--color-text)] w-28 shrink-0">{entry.action}</span>
                <span className="text-xs text-[var(--color-text-muted)] flex-1 truncate">{entry.details}</span>
                <span className="text-xs text-[var(--color-text-muted)] shrink-0">
                  {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                </span>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            page={pag.page}
            totalPages={pag.totalPages}
            total={pag.total}
            perPage={pag.perPage}
            onPageChange={pag.setPage}
            onPerPageChange={pag.setPerPage}
          />
        </>
      )}
    </div>
  );
}
